import { Button, Empty, Icon, Tooltip } from "antd";
import classNames from "classnames";
import React, { useRef, useState } from "react";
import { connect } from "react-redux";
import { WebsitePagesState } from "../../store/websitePages/types";
import { formatAndResizeImageUrl } from "../../helpers/formatAndResizeImageUrl";
import {
  createWebsitePageWaitingForServer,
  pageUrlUpdateErrorMessage,
} from "../../store/websitePages/actions";
import { createAndSavePageAsync } from "../../store/websitePages/thunks";
import {
  EditFillTemplateData,
  GetCurrentStoreData,
  WebsitesState,
} from "../../store/websites/types";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { openNotification } from "../../helpers/openNotification";
import {
  DEFAULT_ERROR_MESSAGE,
  DEFAULT_ERROR_TITLE,
  notificationDuration,
} from "../../data/constants";
import _ from "lodash";
import { editFillTemplateData } from "../../store/websites/actions";
import { getCurrentStoreData } from "../../store/websites/thunks";
import { PosthogEvents } from "../../enums/AnalyticsEventsEnums";
import { formatToURLPath } from "../../helpers/strings/formatToURLPath";
import { generateUniqueUrl } from "../../helpers/generateUniqueUrl";
import { createAndSavePost } from "../../store/blogPosts/thunks";
import LoaderAnimated from "./LoaderAnimated";
import fuzzysort from "fuzzysort";

interface Props extends RouteComponentProps {
  searchValue?: string;
  onSuccess?: (payload?: any) => any;
  setUrlInputValue?: (url: string) => void;
  setSidebarMenu?: (menu: string) => void;

  websitesPages: WebsitePagesState;
  websites: WebsitesState;
  createWebsitePageWaitingForServer: (isWaiting: boolean) => void;
  createAndSavePageAsync: any;
  pageUrlUpdateErrorMessage: (message: string) => void;
  editFillTemplateData: EditFillTemplateData;
  getCurrentStoreData: GetCurrentStoreData;
  currentWebsitePages: any;
  createAndSavePost: any;
}

interface PageTemplateProps extends Props {
  title: string;
  thumbnailUrl?: string;
  templateId?: number;
  description?: string;
  onClick?: () => void;
  defaultUrl?: string;
}
const PageTemplate = (props: PageTemplateProps) => {
  const {
    thumbnailUrl,
    title,
    websitesPages,
    templateId,
    description,
    createWebsitePageWaitingForServer,
    createAndSavePageAsync,
    onSuccess,
    websites,
    history,
    pageUrlUpdateErrorMessage,
    editFillTemplateData,
    getCurrentStoreData,
    currentWebsitePages,
    onClick,
    defaultUrl,
  } = props;
  const itemContainerRef = useRef<HTMLDivElement>(null);
  const { currentWebsite } = getCurrentStoreData();
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  const handleCreatePage = (templateId?: number) => {
    if (websitesPages.isWaitingForCreateWebsitePageResponse) return;
    createWebsitePageWaitingForServer(true);
    let currentPageUrl;
    const currentHomePage = websitesPages.items
      .filter((page) => {
        return page.website === _.get(currentWebsite, "id");
      })
      .find((page) => {
        return page.url === "";
      });
    if (!currentHomePage) {
      currentPageUrl = "";
    } else {
      currentPageUrl = generateUniqueUrl(
        formatToURLPath(defaultUrl) || "untitled",
        currentWebsitePages
      );
    }
    createAndSavePageAsync(currentPageUrl, templateId)
      .then(({ data }) => {
        if (onSuccess) {
          onSuccess();
        }

        if (!templateId) {
          window.posthog.capture(PosthogEvents.USE_PAGE_TEMPLATE, {
            template_name: "Blank page",
            template_page_url: null,
            template_page_id: null,
          });
        } else {
          const usedTemplate = websitesPages.pageTemplatesArray.find(
            (template) => template.page_id === templateId
          );
          if (usedTemplate) {
            window.posthog.capture(PosthogEvents.USE_PAGE_TEMPLATE, {
              template_name: usedTemplate.title,
              template_page_url: usedTemplate.page_url,
              template_page_id: usedTemplate.page_id,
            });
          }
        }

        window.posthog.capture(PosthogEvents.SET_PAGE_URL, {
          page_url: data.url,
        });

        const subdomain = websites.items.find((website) => {
          return website.id === data.website;
        }).subdomain;
        history.push(`/${subdomain}/pages/${data.id}`);
        window.scrollTo(0, 0);
      })
      .catch((error) => {
        console.error(error);

        const urlValidationError = _.get(error, "response.data.url");
        if (urlValidationError) {
          pageUrlUpdateErrorMessage(urlValidationError);
          if (props.setUrlInputValue) {
            props.setUrlInputValue(formatToURLPath(currentPageUrl));
          }
          return;
        }

        const warningMessage =
          _.get(error, "response.data.limit") ||
          _.get(error, "response.data.not_found");
        if (warningMessage) {
          openNotification(
            "Error",
            warningMessage,
            "OK",
            "warning",
            notificationDuration.medium
          );
          return;
        }

        openNotification(
          DEFAULT_ERROR_TITLE,
          DEFAULT_ERROR_MESSAGE,
          "OK",
          "error",
          notificationDuration.medium
        );
      })
      .finally(() => {
        createWebsitePageWaitingForServer(false);
      });
  };

  return (
    <div
      className={classNames("page-template", {
        "has-thumbnaul": !!thumbnailUrl,
        "is-image-loaded": isImageLoaded,
      })}
      ref={itemContainerRef}
      onClick={() => {
        if (onClick) {
          onClick();
          return;
        }
        handleCreatePage(templateId);
      }}
    >
      <Button
        type="primary"
        className="page-template__button"
        loading={websitesPages.isWaitingForCreateWebsitePageResponse}
      >
        Add page
      </Button>
      {!isImageLoaded && (
        <div className="page-template__loader">
          <LoaderAnimated />
        </div>
      )}
      <div className="page-template__top">
        {!!thumbnailUrl && (
          <img
            className="page-template__thumbnail"
            src={thumbnailUrl}
            onLoad={() => {
              setIsImageLoaded(true);
            }}
          />
        )}
        {!thumbnailUrl && <div className="page-template__text-blank">Page</div>}
      </div>
    </div>
  );
};

const PageTemplatesList = (props: Props): JSX.Element => {
  const { websitesPages, searchValue } = props;
  const { currentWebsite, currentBlog } = props.getCurrentStoreData();
  const [isBlogTemplateVisible, setIsBlogTemplateVisible] = useState(
    !currentBlog
  );

  let templates = [
    {
      title: "Blank page",
      thumbnail:
        "https://unicorn-images.b-cdn.net/c99f8e49-2973-4fca-8d9e-14182f2ed033?optimizer=gif&width=350",
      keywords: "Blank empty page",
    },
    {
      title: "Blog",
      thumbnail:
        "https://unicorn-images.b-cdn.net/351357a0-8e0e-41c0-8b6f-5ad8c5f9b4b7?optimizer=gif&width=350",
      keywords: "Blog posts",
      onClick: () => {
        props.createWebsitePageWaitingForServer(true);
        props.createAndSavePost().then(({ postData }) => {
          props.createWebsitePageWaitingForServer(false);
          props.history.push(
            `/${currentWebsite.subdomain}/blog/${postData.id}`
          );
          props.setSidebarMenu("blog");
        });
      },
    },
    ...websitesPages.pageTemplatesArray,
  ].filter((template) => {
    return isBlogTemplateVisible || template.title !== "Blog";
  });

  if (searchValue) {
    templates = fuzzysort
      .go(searchValue, templates, {
        keys: ["keywords"],
      })
      .map((result) => {
        const { obj } = result as any;
        return obj;
      });
  }

  const displayedTemplates = templates.map((template, i) => {
    return (
      <PageTemplate
        key={template.title}
        title={template.title}
        thumbnailUrl={template.thumbnail}
        templateId={template.page_id}
        description={template.description}
        defaultUrl={template.default_url}
        onClick={template.onClick}
        {...props}
      />
    );
  });

  return (
    <div style={{}} className="page-templates__list">
      {displayedTemplates}
      {displayedTemplates.length === 0 && (
        <div className="page-templates__list-empty">
          <Empty description="No results" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: any) => {
  return {
    websitesPages: state.websitesPages,
    websites: state.websites,
  };
};
export default connect(mapStateToProps, {
  createWebsitePageWaitingForServer,
  createAndSavePageAsync,
  pageUrlUpdateErrorMessage,
  editFillTemplateData,
  getCurrentStoreData,
  createAndSavePost,
})(withRouter(PageTemplatesList));
